import * as React from "react";
import PropTypes from "prop-types";
import {
	AppBar,
	Box,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Toolbar,
	Typography,
	Button,
	Menu,
	MenuItem,
	Collapse,
	Link,
	useScrollTrigger,
	useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useNavigate } from "react-router";
import logo from "../../images/logo-no-bg.png";
import { COMPANY_NAME, COMPANY_EMAIL } from "../../constants/contact";
import { useThemeMode } from "../../themes/ThemeContext";

const drawerWidth = 260;

const productSubItems = [
	["Tubular", "/products/tubular"],
	["Flat", "/products/flat"],
	["Other Products", "/products/other"],
];

function DrawerAppBar(props) {
	const { window } = props;
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const [anchorEl, setAnchorEl] = React.useState(null);
	const [openProducts, setOpenProducts] = React.useState(false);
	const navigate = useNavigate();
	const theme = useTheme();
	const { mode, toggleTheme } = useThemeMode();
	const isDark = mode === "dark";

	const scrolled = useScrollTrigger({
		disableHysteresis: true,
		threshold: 50,
	});

	const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
	const handleProductsClick = () => setOpenProducts(!openProducts);

	const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
	const handleMenuClose = () => setAnchorEl(null);

	const drawer = (
		<Box sx={{ textAlign: "center", py: 2 }}>
			<Box
				sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, cursor: "pointer", px: 2, mb: 1 }}
				onClick={() => { navigate("/home"); handleDrawerToggle(); }}
			>
				<img src={logo} alt={`${COMPANY_NAME} logo`} style={{ height: 36 }} />
				<Typography variant="subtitle1" fontWeight={700} sx={{ color: "primary.main" }}>
					{COMPANY_NAME}
				</Typography>
			</Box>
			<Divider />
			<List sx={{ px: 1, mt: 1 }}>
				<ListItem disablePadding>
					<ListItemButton
						sx={{ borderRadius: 2, mb: 0.5 }}
						onClick={() => { navigate("/home"); handleDrawerToggle(); }}
					>
						<ListItemText primary="Home" />
					</ListItemButton>
				</ListItem>
				<ListItem disablePadding>
					<ListItemButton
						sx={{ borderRadius: 2, mb: 0.5 }}
						onClick={() => { navigate("/about-us"); handleDrawerToggle(); }}
					>
						<ListItemText primary="About Us" />
					</ListItemButton>
				</ListItem>
				<ListItem disablePadding>
					<ListItemButton sx={{ borderRadius: 2, mb: 0.5 }} onClick={handleProductsClick}>
						<ListItemText primary="Products" />
						{openProducts ? <ExpandLess /> : <ExpandMore />}
					</ListItemButton>
				</ListItem>
				<Collapse in={openProducts} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{productSubItems.map(([label, path]) => (
							<ListItem key={path} disablePadding>
								<ListItemButton
									sx={{ pl: 4, borderRadius: 2, mb: 0.5 }}
									onClick={() => { navigate(path); handleDrawerToggle(); }}
								>
									<ListItemText primary={label} />
								</ListItemButton>
							</ListItem>
						))}
					</List>
				</Collapse>
				<Divider sx={{ my: 1 }} />
				<ListItem disablePadding>
					<ListItemButton
						sx={{ borderRadius: 2, mb: 0.5 }}
						onClick={toggleTheme}
					>
						<ListItemText primary={isDark ? "Light Mode" : "Dark Mode"} />
						{isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
					</ListItemButton>
				</ListItem>
				<ListItem sx={{ mt: 0.5 }}>
					<Button
						variant="contained"
						fullWidth
						component={Link}
						href={`mailto:${COMPANY_EMAIL}`}
						target="_blank"
						rel="noreferrer"
						startIcon={<EmailIcon />}
						sx={{
							borderRadius: 2,
							textDecoration: "none",
							color: "white",
						}}
						onClick={handleDrawerToggle}
					>
						Contact Us
					</Button>
				</ListItem>
			</List>
		</Box>
	);

	const container =
		window !== undefined ? () => window().document.body : undefined;

	const appBarBg = isDark
		? scrolled ? "rgba(15, 23, 36, 0.95)" : theme.palette.background.paper
		: scrolled ? "rgba(13, 59, 94, 0.95)" : "primary.main";

	return (
		<Box sx={{ display: "flex" }}>
			<AppBar
				component="nav"
				aria-label="Main navigation"
				elevation={scrolled ? 4 : 0}
				sx={{
					backgroundColor: appBarBg,
					backdropFilter: scrolled ? "blur(10px)" : "none",
					transition: "all 0.3s ease",
				}}
			>
				<Toolbar sx={{ py: 0.5 }}>
					<IconButton
						color="inherit"
						aria-label="Open navigation menu"
						edge="start"
						onClick={handleDrawerToggle}
						sx={{ mr: 2, display: { sm: "none" } }}
					>
						<MenuIcon />
					</IconButton>

					<Box
						sx={{
							flexGrow: 1,
							cursor: "pointer",
							display: { xs: "none", sm: "flex" },
							alignItems: "center",
						}}
						onClick={() => navigate("/home")}
					>
						<img
							src={logo}
							alt={`${COMPANY_NAME} logo`}
							style={{ height: 52, margin: "8px 10px 8px 0", transition: "height 0.3s" }}
						/>
						<Typography variant="h6" fontWeight={700} sx={{ color: "white", letterSpacing: "0.01em" }}>
							{COMPANY_NAME}
						</Typography>
					</Box>

					<Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.5 }}>
						<Button
							sx={{ color: "#fff", borderRadius: 2, "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
							onClick={() => navigate("/home")}
						>
							Home
						</Button>
						<Button
							sx={{ color: "#fff", borderRadius: 2, "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
							onClick={() => navigate("/about-us")}
						>
							About Us
						</Button>
						<Button
							sx={{ color: "#fff", borderRadius: 2, "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
							aria-haspopup="true"
							aria-expanded={Boolean(anchorEl)}
							onClick={handleMenuOpen}
						>
							Products
						</Button>
						<Menu
							anchorEl={anchorEl}
							open={Boolean(anchorEl)}
							onClose={handleMenuClose}
							slotProps={{
								paper: {
									sx: { borderRadius: 2, mt: 1, minWidth: 180 },
								},
							}}
						>
							{productSubItems.map(([label, path]) => (
								<MenuItem
									key={path}
									onClick={() => { handleMenuClose(); navigate(path); }}
									sx={{ py: 1.2, px: 2.5 }}
								>
									{label}
								</MenuItem>
							))}
						</Menu>

						<IconButton
							onClick={toggleTheme}
							aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
							sx={{
								color: "white",
								ml: 0.5,
								"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
							}}
						>
							{isDark ? <LightModeIcon /> : <DarkModeIcon />}
						</IconButton>

						<Button
							variant="contained"
							component={Link}
							href={`mailto:${COMPANY_EMAIL}`}
							target="_blank"
							rel="noreferrer"
							startIcon={<EmailIcon />}
							aria-label="Contact us via email"
							sx={{
								ml: 1,
								backgroundColor: "secondary.main",
								borderRadius: "50px",
								textDecoration: "none",
								color: "white",
								px: 2.5,
								"&:hover": { backgroundColor: "secondary.light" },
							}}
						>
							Contact Us
						</Button>
					</Box>
				</Toolbar>
			</AppBar>

			<nav aria-label="Mobile navigation">
				<Drawer
					container={container}
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{ keepMounted: true }}
					sx={{
						display: { xs: "block", sm: "none" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
							borderRadius: "0 16px 16px 0",
						},
					}}
				>
					{drawer}
				</Drawer>
			</nav>

			<Box component="main" sx={{ p: 3 }} />
		</Box>
	);
}

DrawerAppBar.propTypes = {
	window: PropTypes.func,
};

export default DrawerAppBar;
